import React, { useMemo, useState, useCallback, useEffect } from 'react';
import '@fontsource-variable/geist/wght.css';
import {
  ChevronDown,
  ContactRound,
  FolderKey,
  Loader2,
  Trash,
  Pencil,
  Users,
  Signpost,
  Truck,
  FileText,
  Paperclip,
  Sparkles,
  Palette,
  Mail,
  Plus,
  BookOpen,
  Megaphone,
} from 'lucide-react';
import PermissoesCustomersView from './PermissoesCustomersView';
import CustomerFilesPanel, { buildFilesScopeFromRow } from './CustomerFilesPanel';
import CustomerUsersPanel from './CustomerUsersPanel';
import UcHoverTooltipHost from './UcHoverTooltipHost';
import ClienteGrupoSection from './ClienteGrupoSection';
import CustomersProcessosView from './CustomersProcessosView';
import CustomersCustomizacaoConfig from './CustomersCustomizacaoConfig';
import CustomersEmailConfig from './CustomersEmailConfig';
import CustomersOauthConfig from './CustomersOauthConfig';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import TransportadorasCustomersView from './TransportadorasCustomersView';
import CustomersManualClienteView from './CustomersManualClienteView';
import CustomersNoticesView from './CustomersNoticesView';
import { CustomerExperienceView } from '../../customer-experience';
import { CustomersChatView } from '../../customers-chat';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import { IconMessage, IconBrandOauth } from '@tabler/icons-react';
import { useConfigSectionParam } from '../../config-hub';
import ConfigSectionSidebarEdge from '../../config-hub/ConfigSectionSidebarEdge';
import '../../process-config/ConfigStatusView2.css';
import './UsersCustomersView.css';

const VC_SEARCH_MIN = 2;
const NOME_PORTAL_MAX = 30;

/** Adapta Lucide ao EcClickUpFilterChip (que passa `stroke` no estilo Tabler). */
function AddClienteChipIcon({ size = 13, stroke = 1.5, strokeWidth, ...rest }) {
  return <Plus size={size} strokeWidth={strokeWidth ?? stroke} {...rest} />;
}

function chatStatusHint(status) {
  if (status == null || status === 'pending') {
    return 'Seu chat ainda está sendo configurado pela Verve.';
  }
  if (status === 'suspended') {
    return 'Chat temporariamente indisponível — contate o suporte da Verve.';
  }
  if (status === 'error') {
    return 'Houve um problema técnico ao configurar seu chat. Contate o suporte.';
  }
  return '';
}

function canEnableChat(chatwootStatus) {
  return chatwootStatus === 'active';
}

async function apiVc(path, { method = 'GET', body, signal } = {}) {
  const token = localStorage.getItem('authToken');
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
  };
  const res = await fetch(`/api/vc-usr${path}`, {
    method,
    headers,
    signal,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.message || `Erro ${res.status}`);
  }
  return json;
}

function newRowKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `rk-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function rowStableKey(r) {
  if (r.idGrupoRef === 'XXXX') return `solo:${r.primeiro?.codigo}`;
  return `grp:${r.idGrupoRef}`;
}

function mapRowsFromApi(data, prevRows = []) {
  const prevByStable = new Map((prevRows || []).map((p) => [rowStableKey(p), p.rowKey]));
  const rows = data?.rows || [];
  return rows.map((r) => {
    const mapped = {
      primeiro: r.primeiro,
      grupoNome: r.grupoNome,
      outros: r.outros || [],
      hasGrupoVisual: r.hasGrupoVisual,
      idGrupoRef: r.idGrupoRef,
      totalUsuarios: Number(r.totalUsuarios) || 0,
    };
    const sk = rowStableKey(mapped);
    return { ...mapped, rowKey: prevByStable.get(sk) || newRowKey() };
  });
}

function normalizeText(v) {
  if (v == null) return '';
  return String(v)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

const CUSTOMER_MODAL_SECTION_CLIENTE = 'cliente-grupo';
const CUSTOMER_MODAL_SECTION_CHAT = 'chat';
const CUSTOMER_MODAL_SECTION_ARQUIVOS = 'arquivos';
const CUSTOMER_MODAL_SECTION_USUARIOS = 'usuarios';

const CUSTOMER_MODAL_NAV = [
  { id: CUSTOMER_MODAL_SECTION_CLIENTE, label: 'Cliente e grupo', Icon: ContactRound },
  { id: CUSTOMER_MODAL_SECTION_ARQUIVOS, label: 'Arquivos', Icon: FileText },
  { id: CUSTOMER_MODAL_SECTION_USUARIOS, label: 'Usuários', Icon: Users },
  { id: CUSTOMER_MODAL_SECTION_CHAT, label: 'Acesso ao Chat', Icon: IconMessage },
];

const CUSTOMERS_MODULE_SECTIONS = [
  {
    title: 'Cadastros',
    items: [
      { id: 'usuarios', label: 'Clientes', Icon: ContactRound },
      { id: 'transportadoras', label: 'Transportadoras', Icon: Truck },
      { id: 'manual-cliente', label: 'Manual do cliente', Icon: BookOpen },
    ],
  },
  {
    title: 'Processos',
    items: [
      { id: 'chat', label: 'Chat', Icon: IconMessage },
      { id: 'experiencia', label: 'Experiência do cliente', Icon: Sparkles },
      { id: 'permissoes', label: 'Permissões', Icon: FolderKey },
      { id: 'processos', label: 'Processos', Icon: Signpost },
    ],
  },
  {
    title: 'Comunicação',
    items: [{ id: 'publicacoes-avisos', label: 'Publicações e avisos', Icon: Megaphone }],
  },
  {
    title: 'Configurações',
    items: [
      { id: 'oauth', label: 'Autenticação OAuth', Icon: IconBrandOauth },
      { id: 'customizacao', label: 'Customização', Icon: Palette },
      { id: 'email', label: 'Contatos', Icon: Mail },
    ],
  },
];

const CUSTOMERS_MODULE_IDS = CUSTOMERS_MODULE_SECTIONS.flatMap((s) => s.items.map((i) => i.id));

const UC_SEM_GRUPOS = {
  key: 'semGrupos',
  label: 'Sem grupos',
  desc: 'Clientes individuais',
  color: '#0E8A82',
};

const UC_GRUPO_COLOR = '#6E56CF';

export default function UsersCustomersView() {
  const [query, setQuery] = useState('');
  const [mainRows, setMainRows] = useState([]);
  const [mainLoading, setMainLoading] = useState(true);
  const [mainError, setMainError] = useState(null);

  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [semGruposCollapsed, setSemGruposCollapsed] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalQuery, setModalQuery] = useState('');
  const [modalDisponiveis, setModalDisponiveis] = useState([]);
  const [modalListLoading, setModalListLoading] = useState(false);
  const [modalSelectedIds, setModalSelectedIds] = useState(() => new Set());
  const [modalSelectedCache, setModalSelectedCache] = useState(() => new Map());
  const [modalGrupo, setModalGrupo] = useState('');
  const [modalNomePortal, setModalNomePortal] = useState('');
  const [modalNomePortalInicial, setModalNomePortalInicial] = useState('');
  const [modalSaving, setModalSaving] = useState(false);
  const [modalEditSingleCodigo, setModalEditSingleCodigo] = useState(null);
  const [deletingKeys, setDeletingKeys] = useState(() => new Set());

  const [editGroupRowKey, setEditGroupRowKey] = useState(null);
  const [editGroupIdGrupoRef, setEditGroupIdGrupoRef] = useState(null);
  const [editGroupDisponiveis, setEditGroupDisponiveis] = useState([]);
  const [editGroupQuery, setEditGroupQuery] = useState('');
  const [editGroupSelectedIds, setEditGroupSelectedIds] = useState(() => new Set());
  const [editGroupSelectedCache, setEditGroupSelectedCache] = useState(() => new Map());
  const [editGroupSaving, setEditGroupSaving] = useState(false);
  const [editGroupNome, setEditGroupNome] = useState('');
  const [editGroupNomeInicial, setEditGroupNomeInicial] = useState('');
  const [editGroupNomePortal, setEditGroupNomePortal] = useState('');
  const [editGroupNomePortalInicial, setEditGroupNomePortalInicial] = useState('');
  const [editGroupSearchLoading, setEditGroupSearchLoading] = useState(false);
  const [editGroupRemovingCodigo, setEditGroupRemovingCodigo] = useState(null);

  const [chatSettings, setChatSettings] = useState(null);
  const [chatSettingsLoading, setChatSettingsLoading] = useState(false);
  const [chatSettingsSaving, setChatSettingsSaving] = useState(false);
  const [chatSettingsError, setChatSettingsError] = useState(null);

  const [customerModalSection, setCustomerModalSection] = useState(CUSTOMER_MODAL_SECTION_CLIENTE);

  const [activeModule, setActiveModule] = useConfigSectionParam('usuarios', CUSTOMERS_MODULE_IDS);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const toggleExpanded = useCallback((rowKey) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }, []);

  const loadMain = useCallback(async () => {
    setMainLoading(true);
    setMainError(null);
    try {
      const j = await apiVc('/listagem');
      setMainRows((prev) => mapRowsFromApi(j.data, prev));
    } catch (e) {
      setMainError(e.message || 'Falha ao carregar');
      setMainRows([]);
    } finally {
      setMainLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMain();
  }, [loadMain]);

  const mainRowsFlatForFilter = useMemo(
    () =>
      mainRows.map((r) => ({
        ...r,
        _blob: normalizeText([
          r.primeiro?.codigo,
          r.primeiro?.cnpj,
          r.primeiro?.nomePortal,
          r.primeiro?.razaoSocial,
          r.grupoNome,
          r.idGrupoRef,
          r.primeiro?.contratos,
          r.primeiro?.usuarios,
          ...(r.outros || []).flatMap((o) => [
            o.codigo,
            o.cnpj,
            o.nomePortal,
            o.razaoSocial,
            o.contratos,
            o.usuarios,
          ]),
        ].filter(Boolean).join(' ')),
      })),
    [mainRows]
  );

  const filteredMain = useMemo(() => {
    const q = normalizeText(query);
    if (!q) return mainRowsFlatForFilter;
    return mainRowsFlatForFilter.filter((r) => r._blob.includes(q));
  }, [mainRowsFlatForFilter, query]);

  const groupedMain = useMemo(() => {
    const grupos = [];
    const semGrupos = [];
    filteredMain.forEach((r) => {
      if (r.hasGrupoVisual) grupos.push(r);
      else semGrupos.push(r);
    });
    return { grupos, semGrupos };
  }, [filteredMain]);

  const editGroupRow = useMemo(
    () => (editGroupRowKey == null ? null : mainRows.find((r) => r.rowKey === editGroupRowKey) || null),
    [mainRows, editGroupRowKey]
  );

  const editGroupIntegrantes = useMemo(() => {
    if (!editGroupRow) return [];
    return [editGroupRow.primeiro, ...(editGroupRow.outros || [])].filter(Boolean);
  }, [editGroupRow]);

  const editGroupPendingIntegrantes = useMemo(() => {
    const existing = new Set(editGroupIntegrantes.map((c) => String(c.codigo || c.id).trim()));
    return [...editGroupSelectedIds]
      .map((id) => editGroupSelectedCache.get(id))
      .filter((c) => c && !existing.has(String(c.codigo || c.id).trim()));
  }, [editGroupSelectedIds, editGroupSelectedCache, editGroupIntegrantes]);

  const modalPendingIntegrantes = useMemo(
    () =>
      [...modalSelectedIds]
        .map((id) => modalSelectedCache.get(id))
        .filter(Boolean),
    [modalSelectedIds, modalSelectedCache]
  );

  const customerModalEmbeddedScope = useMemo(() => {
    if (editGroupRowKey != null && editGroupRow) {
      return buildFilesScopeFromRow(editGroupRow);
    }
    if (modalOpen && modalEditSingleCodigo) {
      const row = mainRows.find(
        (r) => String(r.primeiro?.codigo || '').trim() === modalEditSingleCodigo
      );
      return row ? buildFilesScopeFromRow(row) : null;
    }
    return null;
  }, [editGroupRowKey, editGroupRow, modalOpen, modalEditSingleCodigo, mainRows]);

  const refreshFromListagemPayload = useCallback((payload) => {
    setMainRows((prev) => mapRowsFromApi(payload, prev));
  }, []);

  const modalSelectedCount = modalSelectedIds.size;
  const modalNomePortalSoloChanged =
    Boolean(modalEditSingleCodigo) &&
    modalSelectedCount === 1 &&
    modalNomePortal.trim() !== modalNomePortalInicial.trim();
  const canSaveModal =
    modalSelectedCount > 0 &&
    (modalNomePortalSoloChanged ||
      (modalEditSingleCodigo
        ? modalSelectedCount >= 2 && modalGrupo.trim().length >= 3
        : modalSelectedCount === 1 || modalGrupo.trim().length >= 3));

  useEffect(() => {
    if (
      modalOpen &&
      modalSelectedCount === 0 &&
      customerModalSection === CUSTOMER_MODAL_SECTION_CHAT
    ) {
      setCustomerModalSection(CUSTOMER_MODAL_SECTION_CLIENTE);
    }
  }, [modalOpen, modalSelectedCount, customerModalSection]);

  useEffect(() => {
    if (!customerModalEmbeddedScope && customerModalSection === CUSTOMER_MODAL_SECTION_ARQUIVOS) {
      setCustomerModalSection(CUSTOMER_MODAL_SECTION_CLIENTE);
    }
    if (!customerModalEmbeddedScope && customerModalSection === CUSTOMER_MODAL_SECTION_USUARIOS) {
      setCustomerModalSection(CUSTOMER_MODAL_SECTION_CLIENTE);
    }
  }, [customerModalEmbeddedScope, customerModalSection]);

  const editGroupHasSelected = editGroupSelectedIds.size > 0;
  const editGroupNomeTrim = editGroupNome.trim();
  const editGroupNomeInicialTrim = editGroupNomeInicial.trim();
  const editGroupNomeChanged = editGroupNomeTrim !== editGroupNomeInicialTrim;
  const editGroupNomeOk = editGroupNomeTrim.length >= 3;
  const editGroupNomePortalChanged =
    editGroupNomePortal.trim() !== editGroupNomePortalInicial.trim();
  const canSaveEditGroup =
    Boolean(editGroupIdGrupoRef) &&
    (editGroupHasSelected ||
      (editGroupNomeChanged && editGroupNomeOk) ||
      editGroupNomePortalChanged);

  const openAddModal = () => {
    setCustomerModalSection(CUSTOMER_MODAL_SECTION_CLIENTE);
    setEditGroupRowKey(null);
    setEditGroupIdGrupoRef(null);
    setEditGroupDisponiveis([]);
    setEditGroupQuery('');
    setEditGroupSelectedIds(new Set());
    setEditGroupSelectedCache(new Map());
    setModalEditSingleCodigo(null);
    setModalQuery('');
    setModalGrupo('');
    setModalNomePortal('');
    setModalNomePortalInicial('');
    setModalSelectedIds(new Set());
    setModalSelectedCache(new Map());
    setModalDisponiveis([]);
    setModalOpen(true);
    setModalListLoading(false);
  };

  const openEditSingleModal = (row) => {
    const cod = row?.primeiro?.codigo;
    if (!cod) return;
    const first = row.primeiro;
    const id = String(cod).trim();
    setCustomerModalSection(CUSTOMER_MODAL_SECTION_CLIENTE);
    setEditGroupRowKey(null);
    setEditGroupIdGrupoRef(null);
    setEditGroupDisponiveis([]);
    setEditGroupQuery('');
    setEditGroupSelectedIds(new Set());
    setEditGroupSelectedCache(new Map());
    setModalEditSingleCodigo(id);
    setModalQuery('');
    setModalGrupo('');
    const np0 = String(first?.nomePortal || '').slice(0, NOME_PORTAL_MAX);
    setModalNomePortal(np0);
    setModalNomePortalInicial(np0);
    setModalSelectedIds(new Set([id]));
    setModalSelectedCache(
      new Map([
        [
          id,
          {
            id,
            codigo: first.codigo,
            cnpj: first.cnpj,
            razaoSocial: first.razaoSocial,
            contratos: first.contratos,
          },
        ],
      ])
    );
    setModalDisponiveis([]);
    setModalOpen(true);
    setModalListLoading(false);
  };

  const openEditGroupModal = (rowKey) => {
    const row = mainRows.find((x) => x.rowKey === rowKey);
    setCustomerModalSection(CUSTOMER_MODAL_SECTION_CLIENTE);
    setModalOpen(false);
    setEditGroupRowKey(rowKey);
    setEditGroupIdGrupoRef(row?.idGrupoRef && row.idGrupoRef !== 'XXXX' ? row.idGrupoRef : null);
    setEditGroupNome(row?.grupoNome || '');
    setEditGroupNomeInicial(row?.grupoNome || '');
    const np0 = String(row?.primeiro?.nomePortal || '').slice(0, NOME_PORTAL_MAX);
    setEditGroupNomePortal(np0);
    setEditGroupNomePortalInicial(np0);
    setEditGroupQuery('');
    setEditGroupSelectedIds(new Set());
    setEditGroupSelectedCache(new Map());
    setEditGroupDisponiveis([]);
    setEditGroupSearchLoading(false);
    setModalListLoading(false);
  };

  useEffect(() => {
    if (!modalOpen) return undefined;
    const q = modalQuery.trim();
    if (q.length < VC_SEARCH_MIN) {
      setModalDisponiveis([]);
      setModalListLoading(false);
      return undefined;
    }
    setModalListLoading(true);
    const ac = new AbortController();
    const t = setTimeout(async () => {
      try {
        const j = await apiVc(`/clientes-disponiveis?q=${encodeURIComponent(q)}`, { signal: ac.signal });
        setModalDisponiveis(Array.isArray(j.data) ? j.data : []);
      } catch (e) {
        if (e.name === 'AbortError') return;
        setModalDisponiveis([]);
      } finally {
        setModalListLoading(false);
      }
    }, 320);
    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [modalOpen, modalQuery]);

  useEffect(() => {
    if (editGroupRowKey == null) return undefined;
    const q = editGroupQuery.trim();
    if (q.length < VC_SEARCH_MIN) {
      setEditGroupDisponiveis([]);
      setEditGroupSearchLoading(false);
      return undefined;
    }
    setEditGroupSearchLoading(true);
    const ac = new AbortController();
    const t = setTimeout(async () => {
      try {
        const j = await apiVc(`/clientes-disponiveis?q=${encodeURIComponent(q)}`, { signal: ac.signal });
        setEditGroupDisponiveis(Array.isArray(j.data) ? j.data : []);
      } catch (e) {
        if (e.name === 'AbortError') return;
        setEditGroupDisponiveis([]);
      } finally {
        setEditGroupSearchLoading(false);
      }
    }, 320);
    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [editGroupRowKey, editGroupQuery]);

  useEffect(() => {
    const modalActive = editGroupRowKey != null || modalOpen;
    if (!modalActive) {
      setChatSettings(null);
      setChatSettingsLoading(false);
      setChatSettingsError(null);
      return undefined;
    }

    let cancelled = false;
    setChatSettingsLoading(true);
    setChatSettingsError(null);

    (async () => {
      try {
        const j = await apiVc('/chat-settings');
        if (!cancelled) setChatSettings(j.data || null);
      } catch (e) {
        if (!cancelled) {
          setChatSettings(null);
          setChatSettingsError(e.message || 'Erro ao carregar chat');
        }
      } finally {
        if (!cancelled) setChatSettingsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [editGroupRowKey, modalOpen]);

  const handleChatToggle = useCallback(async (nextEnabled) => {
    const prev = chatSettings;
    setChatSettings((cur) => (cur ? { ...cur, chatEnabled: nextEnabled } : cur));
    setChatSettingsSaving(true);
    setChatSettingsError(null);
    try {
      const j = await apiVc('/chat-settings', {
        method: 'PUT',
        body: { enabled: nextEnabled },
      });
      setChatSettings(j.data || null);
    } catch (e) {
      setChatSettings(prev);
      setChatSettingsError(e.message || 'Erro ao salvar chat');
      window.alert(e.message || 'Erro ao salvar configuração de chat');
    } finally {
      setChatSettingsSaving(false);
    }
  }, [chatSettings]);

  useEffect(() => {
    if (editGroupRowKey == null || editGroupSaving || editGroupRemovingCodigo) return;
    const r = mainRows.find((x) => x.rowKey === editGroupRowKey);
    if (!r) {
      setEditGroupRowKey(null);
      setEditGroupIdGrupoRef(null);
      setEditGroupDisponiveis([]);
      setEditGroupQuery('');
      setEditGroupSelectedIds(new Set());
      setEditGroupSelectedCache(new Map());
    }
  }, [mainRows, editGroupRowKey, editGroupSaving, editGroupRemovingCodigo]);

  const closeEditGroupModal = () => {
    if (editGroupSaving || editGroupRemovingCodigo) return;
    setCustomerModalSection(CUSTOMER_MODAL_SECTION_CLIENTE);
    setEditGroupRowKey(null);
    setEditGroupIdGrupoRef(null);
    setEditGroupDisponiveis([]);
    setEditGroupQuery('');
    setEditGroupSelectedIds(new Set());
    setEditGroupSelectedCache(new Map());
    setEditGroupNome('');
    setEditGroupNomeInicial('');
    setEditGroupNomePortal('');
    setEditGroupNomePortalInicial('');
    setEditGroupSearchLoading(false);
  };

  const removeIntegranteDoGrupo = useCallback(async (codigo) => {
    const cod = String(codigo || '').trim();
    if (!cod) return;
    if (!window.confirm('Remover este cliente do grupo?')) return;
    setEditGroupRemovingCodigo(cod);
    try {
      const j = await apiVc(`/cliente/${encodeURIComponent(cod)}`, { method: 'DELETE' });
      setMainRows((prev) => mapRowsFromApi(j.data, prev));
      setEditGroupSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(cod);
        return next;
      });
    } catch (e) {
      window.alert(e.message || 'Erro ao remover cliente');
    } finally {
      setEditGroupRemovingCodigo(null);
    }
  }, []);

  const toggleEditGroupSelect = (client) => {
    if (!client || client.jaVinculado) return;
    const id = client.id;
    setEditGroupSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setEditGroupSelectedCache((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, client);
      return next;
    });
  };

  const closeModal = () => {
    if (modalSaving) return;
    setCustomerModalSection(CUSTOMER_MODAL_SECTION_CLIENTE);
    setModalOpen(false);
    setModalQuery('');
    setModalGrupo('');
    setModalNomePortal('');
    setModalNomePortalInicial('');
    setModalSelectedIds(new Set());
    setModalSelectedCache(new Map());
    setModalDisponiveis([]);
    setModalEditSingleCodigo(null);
  };

  const toggleModalSelect = (client) => {
    if (!client || client.jaVinculado) return;
    const id = client.id;
    setModalSelectedIds((prev) => {
      if (modalEditSingleCodigo && id === modalEditSingleCodigo && prev.has(id)) return prev;
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setModalSelectedCache((prev) => {
      if (modalEditSingleCodigo && id === modalEditSingleCodigo && prev.has(id)) return prev;
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, client);
      return next;
    });
  };

  const handleModalSave = async () => {
    if (!canSaveModal) return;
    const ids = [...modalSelectedIds];
    if (ids.length === 0) return;

    const npTrim = modalNomePortal.trim();
    if (npTrim.length > NOME_PORTAL_MAX) {
      window.alert(`Nome portal: no máximo ${NOME_PORTAL_MAX} caracteres.`);
      return;
    }

    setModalSaving(true);
    try {
      const nomeAlvo = ids.length > 1 ? modalGrupo.trim() : null;
      let j;
      if (modalEditSingleCodigo && ids.length === 1) {
        // Edição do cliente já cadastrado: só atualiza o nome no portal
        // (nunca recadastrar, senão os usuários vinculados são removidos).
        j = await apiVc('/cliente/nome-portal', {
          method: 'POST',
          body: { codigos: ids, nomePortal: npTrim },
        });
      } else if (modalEditSingleCodigo) {
        // Conversão "solo" -> grupo novo preservando os usuários vinculados.
        j = await apiVc('/cliente/converter-grupo', {
          method: 'POST',
          body: {
            codigos: ids,
            grupoNome: modalGrupo.trim(),
            nomePortal: npTrim || undefined,
          },
        });
      } else {
        j = await apiVc('/cadastrar', {
          method: 'POST',
          body: {
            codigos: ids,
            grupoNome: ids.length > 1 ? modalGrupo.trim() : null,
            nomePortal: npTrim || undefined,
          },
        });
      }
      setMainRows((prev) => {
        const mapped = mapRowsFromApi(j.data, prev);
        if (nomeAlvo) {
          const hit = mapped.find((row) => row.hasGrupoVisual && row.grupoNome === nomeAlvo);
          if (hit) setExpandedKeys((e) => new Set(e).add(hit.rowKey));
        }
        return mapped;
      });
      setModalOpen(false);
      setModalQuery('');
      setModalGrupo('');
      setModalSelectedIds(new Set());
      setModalSelectedCache(new Map());
      setModalDisponiveis([]);
      setModalEditSingleCodigo(null);
    } catch (e) {
      window.alert(e.message || 'Erro ao salvar');
    } finally {
      setModalSaving(false);
    }
  };

  const handleEditGroupSave = async () => {
    if (!editGroupRowKey || !editGroupIdGrupoRef) return;
    const codigos = [...editGroupSelectedIds];
    const nomeTrim = editGroupNome.trim();
    const nomeInicialTrim = editGroupNomeInicial.trim();
    const nomeChanged = nomeTrim !== nomeInicialTrim;
    if (nomeChanged && nomeTrim.length < 3) {
      window.alert('Nome do grupo obrigatório (3+ caracteres)');
      return;
    }
    const npTrim = editGroupNomePortal.trim();
    if (npTrim.length > NOME_PORTAL_MAX) {
      window.alert(`Nome portal: no máximo ${NOME_PORTAL_MAX} caracteres.`);
      return;
    }
    const portalCh = npTrim !== editGroupNomePortalInicial.trim();
    setEditGroupSaving(true);
    try {
      let lastPayload = null;
      if (nomeChanged) {
        const jNome = await apiVc('/grupo/nome', {
          method: 'POST',
          body: { idGrupoRef: editGroupIdGrupoRef, nome: nomeTrim },
        });
        lastPayload = jNome.data;
      }
      if (portalCh) {
        const jPortal = await apiVc('/grupo/nome-portal', {
          method: 'POST',
          body: { idGrupoRef: editGroupIdGrupoRef, nomePortal: npTrim },
        });
        lastPayload = jPortal.data;
      }
      if (codigos.length > 0) {
        const j = await apiVc('/grupo/adicionar', {
          method: 'POST',
          body: {
            idGrupoRef: editGroupIdGrupoRef,
            codigos,
            ...(portalCh ? { nomePortal: npTrim } : {}),
          },
        });
        lastPayload = j.data;
      }

      if (lastPayload) setMainRows((prev) => mapRowsFromApi(lastPayload, prev));
      setExpandedKeys((prev) => {
        const next = new Set(prev);
        next.add(editGroupRowKey);
        return next;
      });
      setEditGroupRowKey(null);
      setEditGroupIdGrupoRef(null);
      setEditGroupDisponiveis([]);
      setEditGroupQuery('');
      setEditGroupSelectedIds(new Set());
      setEditGroupSelectedCache(new Map());
      setEditGroupNome('');
      setEditGroupNomeInicial('');
      setEditGroupNomePortal('');
      setEditGroupNomePortalInicial('');
    } catch (e) {
      window.alert(e.message || 'Erro ao salvar');
    } finally {
      setEditGroupSaving(false);
    }
  };

  const requestDelete = useCallback((row, subCliente) => {
    const isSub = Boolean(subCliente);
    const animKey = isSub ? `sub:${row.rowKey}:${subCliente.id}` : `row:${row.rowKey}`;
    let msg;
    if (isSub) {
      msg = `Excluir o cliente "${subCliente.razaoSocial}" deste grupo?`;
    } else if (row.hasGrupoVisual) {
      msg = `Excluir o grupo "${row.grupoNome}" e todos os clientes vinculados?`;
    } else {
      msg = `Excluir o cliente "${row.primeiro.razaoSocial}"?`;
    }
    if (!window.confirm(msg)) return;

    setDeletingKeys((prev) => new Set(prev).add(animKey));

    (async () => {
      try {
        let j;
        if (isSub) {
          j = await apiVc(`/cliente/${encodeURIComponent(subCliente.codigo)}`, { method: 'DELETE' });
        } else if (row.hasGrupoVisual) {
          j = await apiVc(`/grupo/${encodeURIComponent(row.idGrupoRef)}`, { method: 'DELETE' });
        } else {
          j = await apiVc(`/cliente/${encodeURIComponent(row.primeiro.codigo)}`, { method: 'DELETE' });
        }
        setMainRows((prev) => mapRowsFromApi(j.data, prev));
        setExpandedKeys((prev) => {
          const next = new Set(prev);
          if (!isSub) next.delete(row.rowKey);
          return next;
        });
      } catch (e) {
        window.alert(e.message || 'Erro ao excluir');
      } finally {
        setDeletingKeys((prev) => {
          const next = new Set(prev);
          next.delete(animKey);
          return next;
        });
      }
    })();
  }, []);

  const customerModalSectionTitle =
    customerModalSection === CUSTOMER_MODAL_SECTION_CHAT
      ? 'Acesso ao Chat'
      : customerModalSection === CUSTOMER_MODAL_SECTION_ARQUIVOS
        ? 'Gestão de arquivos'
        : customerModalSection === CUSTOMER_MODAL_SECTION_USUARIOS
          ? 'Gerenciar usuários'
          : 'Cliente e grupo';

  const renderCustomerModalSidebar = (
    title,
    subtitle,
    { showChatAccess = true, showFilesAccess = true, showUsersAccess = true, titleId } = {}
  ) => {
    const navItems = CUSTOMER_MODAL_NAV.filter((item) => {
      if (item.id === CUSTOMER_MODAL_SECTION_CHAT && !showChatAccess) return false;
      if (item.id === CUSTOMER_MODAL_SECTION_ARQUIVOS && !showFilesAccess) return false;
      if (item.id === CUSTOMER_MODAL_SECTION_USUARIOS && !showUsersAccess) return false;
      return true;
    });

    return (
      <aside className="uc-customer-modal-sidebar" aria-label="Seções do cliente">
        <div className="uc-customer-modal-sidebar-header">
          <h2 id={titleId} className="uc-customer-modal-sidebar-title">
            {title}
          </h2>
          <p className="uc-customer-modal-sidebar-subtitle">{subtitle}</p>
        </div>
        <nav className="uc-customer-modal-nav">
          {navItems.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              className={[
                'uc-customer-modal-nav-item',
                customerModalSection === id ? 'active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => setCustomerModalSection(id)}
            >
              <span className="uc-customer-modal-nav-icon" aria-hidden>
                <Icon size={14} strokeWidth={2} />
              </span>
              {label}
            </button>
          ))}
        </nav>
      </aside>
    );
  };

  const renderCustomerModalMainHead = ({ onClose, closeDisabled }) => (
    <div className="uc-customer-modal-main-head">
      <h3 className="uc-customer-modal-main-title">{customerModalSectionTitle}</h3>
      <button
        type="button"
        className="uc-customer-modal-close"
        onClick={onClose}
        disabled={closeDisabled}
        aria-label="Fechar"
        title="Fechar"
      >
        ✕
      </button>
    </div>
  );

  const renderFilesSection = () =>
    customerModalEmbeddedScope ? (
      <CustomerFilesPanel
        modal={customerModalEmbeddedScope}
        onListagemRefresh={refreshFromListagemPayload}
        embedded
      />
    ) : null;

  const renderUsersSection = () =>
    customerModalEmbeddedScope ? (
      <CustomerUsersPanel
        scope={customerModalEmbeddedScope}
        onListagemRefresh={loadMain}
        embedded
      />
    ) : null;

  const renderChatAccessSection = () => (
    <div className="uc-chat-settings-panel">
      <div className="uc-chat-settings-row">
        <div className="uc-chat-settings-copy">
          <h4 className="uc-chat-settings-title">Chat com clientes finais</h4>
          <p className="uc-chat-settings-desc">
            {canEnableChat(chatSettings?.chatwootStatus)
              ? 'Habilite o chat para que os clientes do portal possam conversar com sua equipe.'
              : chatStatusHint(chatSettings?.chatwootStatus)}
          </p>
        </div>
        {chatSettingsLoading ? (
          <Loader2 className="uc-chat-toggle-spinner" size={18} aria-hidden />
        ) : (
          <label
            className={[
              'uc-chat-toggle',
              !canEnableChat(chatSettings?.chatwootStatus) ? 'uc-chat-toggle--disabled' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            title={
              canEnableChat(chatSettings?.chatwootStatus)
                ? 'Habilitar chat para clientes finais'
                : chatStatusHint(chatSettings?.chatwootStatus)
            }
          >
            <input
              type="checkbox"
              checked={Boolean(chatSettings?.chatEnabled)}
              disabled={
                chatSettingsSaving ||
                !canEnableChat(chatSettings?.chatwootStatus) ||
                chatSettings == null
              }
              onChange={(e) => handleChatToggle(e.target.checked)}
              aria-label="Habilitar chat"
            />
            <span className="uc-chat-toggle-pill" aria-hidden>
              <span className="uc-chat-toggle-dot" />
            </span>
          </label>
        )}
      </div>
      {chatSettingsError && !chatSettingsLoading && (
        <p className="uc-chat-toggle-hint uc-chat-toggle-hint--error" role="alert">
          {chatSettingsError}
        </p>
      )}
    </div>
  );

  const renderActionCell = (row, subCliente) => {
    const isSub = Boolean(subCliente);
    const showEditGrupo = !isSub && row.hasGrupoVisual;
    const showEditSingle = !isSub && !row.hasGrupoVisual;
    const animKey = isSub ? `sub:${row.rowKey}:${subCliente.id}` : `row:${row.rowKey}`;
    const isDeleting = deletingKeys.has(animKey);
    return (
      <td
        className={`uc-cell-action uc-cell-sticky-action ${isSub ? 'uc-cell-action--no-manage' : ''}`}
      >
        <div className="uc-action-btns">
          {showEditGrupo && (
            <button
              type="button"
              className="users-customers-edit-grupo-btn"
              onClick={() => openEditGroupModal(row.rowKey)}
              aria-label="Editar grupo — adicionar clientes"
            >
              <Pencil size={14} strokeWidth={2} />
            </button>
          )}
          {showEditSingle && (
            <button
              type="button"
              className="users-customers-edit-grupo-btn"
              onClick={() => openEditSingleModal(row)}
              aria-label="Editar cliente — formar novo grupo"
            >
              <Pencil size={14} strokeWidth={2} />
            </button>
          )}
          <button
            type="button"
            className="users-customers-delete-btn"
            onClick={() => requestDelete(row, isSub ? subCliente : null)}
            disabled={isDeleting}
            aria-label="Excluir"
          >
            <Trash size={14} strokeWidth={2} />
          </button>
        </div>
      </td>
    );
  };

  const renderMainRowCells = (row, { isSub, subCliente } = {}) => {
    const c = isSub ? subCliente : row.primeiro;

    return (
      <>
        <td className={`uc-cell-mono uc-cell-codigo ${isSub ? 'uc-cell-codigo--sub' : ''}`}>
          <div className="uc-codigo-inner">
            <span className="uc-codigo-value">{c.codigo}</span>
          </div>
        </td>
        <td className={`uc-cell-mono ${isSub ? 'uc-cell--sub' : ''}`}>{c.cnpj}</td>
        <td className={`uc-cell-truncate uc-cell-nome-portal ${isSub ? 'uc-cell--sub' : ''}`}>
          <span title={c.nomePortal || ''}>{c.nomePortal || '—'}</span>
        </td>
        <td className={`uc-cell-truncate ${isSub ? 'uc-cell-razao--sub' : ''}`}>
          <span className="uc-razao-with-files" title={c.razaoSocial}>
            <span className="uc-razao-text">{c.razaoSocial}</span>
            {c.temArquivos && (
              <UcHoverTooltipHost
                className="uc-has-files-icon"
                tooltip="Possui arquivos vinculados"
                aria-label="Possui arquivos vinculados"
              >
                <Paperclip size={12} strokeWidth={2} aria-hidden />
              </UcHoverTooltipHost>
            )}
          </span>
        </td>
        <td className={`uc-cell-center uc-cell-sticky-contratos ${isSub ? 'uc-cell--sub' : ''}`}>{c.contratos}</td>
        <td className={`uc-cell-center uc-cell-sticky-usuarios ${isSub ? 'uc-cell--sub' : ''}`}>{c.usuarios}</td>
        {renderActionCell(row, isSub ? subCliente : null)}
      </>
    );
  };

  const renderGrupoSectionHeader = (row) => {
    const expanded = expandedKeys.has(row.rowKey);
    const integrantes = [row.primeiro, ...row.outros].filter(Boolean);
    const nome = String(row.grupoNome || '').trim() || 'Grupo';
    const masterDelKey = `row:${row.rowKey}`;
    const isDeleting = deletingKeys.has(masterDelKey);
    const totalContratos = integrantes.reduce((s, x) => s + (Number(x.contratos) || 0), 0);
    const totalUsuarios = Number(row.totalUsuarios) || 0;
    return (
      <tr
        className={[
          'uc-section-row',
          'uc-section-row--grupo',
          expanded ? '' : 'uc-section-row--collapsed',
          isDeleting ? 'users-customers-row--deleting' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => toggleExpanded(row.rowKey)}
      >
        <td colSpan={4}>
          <div className="uc-section-label">
            <span className="uc-section-chev" aria-hidden>
              <ChevronDown size={13} strokeWidth={2.5} />
            </span>
            <span
              className="uc-section-dot"
              style={{ background: UC_GRUPO_COLOR }}
              aria-hidden
            />
            <span className="uc-section-title" title={nome}>
              {nome}
            </span>
            <span className="uc-section-count">· {integrantes.length}</span>
            {row.temArquivos ? (
              <UcHoverTooltipHost
                className="uc-has-files-icon uc-section-files"
                tooltip="Possui arquivos vinculados"
                aria-label="Possui arquivos vinculados"
              >
                <Paperclip size={12} strokeWidth={2} aria-hidden />
              </UcHoverTooltipHost>
            ) : null}
          </div>
        </td>
        <td className="uc-cell-center uc-cell-sticky-contratos uc-section-total">
          {totalContratos}
        </td>
        <td className="uc-cell-center uc-cell-sticky-usuarios uc-section-total">
          {totalUsuarios}
        </td>
        <td className="uc-cell-action uc-cell-sticky-action">
          <div
            className="uc-section-actions uc-section-actions--cell"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="users-customers-edit-grupo-btn"
              onClick={() => openEditGroupModal(row.rowKey)}
              aria-label="Editar grupo — adicionar clientes"
            >
              <Pencil size={14} strokeWidth={2} />
            </button>
            <button
              type="button"
              className="users-customers-delete-btn"
              onClick={() => requestDelete(row, null)}
              disabled={isDeleting}
              aria-label="Excluir grupo"
            >
              <Trash size={14} strokeWidth={2} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="process-config-root">
      <aside
        className={`process-sidebar${sidebarCollapsed ? ' is-collapsed' : ''}`}
        aria-label="Módulos Customers"
      >
        <ConfigSectionSidebarEdge
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((v) => !v)}
        />
        <nav className="process-submenu">
          {CUSTOMERS_MODULE_SECTIONS.map((section) => (
            <div key={section.title} className="customers-submenu-section">
              <div className="customers-submenu-section-title" aria-hidden={sidebarCollapsed}>
                {section.title}
              </div>
              {section.items.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  className={[
                    'process-submenu-item',
                    activeModule === id ? 'active' : '',
                    sidebarCollapsed ? 'customers-sidebar-tooltip-host' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setActiveModule(id)}
                  data-tooltip={sidebarCollapsed ? label : undefined}
                  aria-label={sidebarCollapsed ? label : undefined}
                >
                  <Icon size={17} strokeWidth={1.75} aria-hidden />
                  <span className="process-submenu-label">{label}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>
      <div
        className={[
          'process-content-area',
          activeModule === 'customizacao' ||
          activeModule === 'processos' ||
          activeModule === 'oauth' ||
          activeModule === 'email' ||
          activeModule === 'usuarios' ||
          activeModule === 'permissoes' ||
          activeModule === 'transportadoras'
            ? 'process-content-area-config-status-2'
            : '',
          'customers-module-content',
          activeModule === 'experiencia' ||
          activeModule === 'chat' ||
          activeModule === 'manual-cliente' ||
          activeModule === 'publicacoes-avisos'
            ? 'customers-module-content--embedded'
            : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {activeModule === 'experiencia' && <CustomerExperienceView embedded />}

        {activeModule === 'chat' && <CustomersChatView embedded />}

        {activeModule === 'manual-cliente' && <CustomersManualClienteView />}

        {activeModule === 'publicacoes-avisos' && <CustomersNoticesView />}

        {activeModule === 'transportadoras' && <TransportadorasCustomersView />}

        {activeModule === 'permissoes' && <PermissoesCustomersView />}

        {activeModule === 'processos' && <CustomersProcessosView />}

        {activeModule === 'customizacao' && <CustomersCustomizacaoConfig />}

        {activeModule === 'email' && <CustomersEmailConfig />}

        {activeModule === 'oauth' && <CustomersOauthConfig />}

        {activeModule === 'usuarios' && (
    <div className="pcs2-page users-customers-page">
      <section className="pcs2-section users-customers-section">
        <div className="pcs2-section-aside">
          <div className="pcs2-section-heading">
            <ContactRound size={16} strokeWidth={1.75} aria-hidden />
            <h3 className="pcs2-section-title">Clientes</h3>
          </div>
          <p className="pcs2-section-desc">Gerencie clientes e usuários do Customers</p>
        </div>

      <div className="users-customers-table-bar">
        <p className="pcs2-subdesc users-customers-table-bar-desc">
          Liste, filtre e cadastre clientes do portal
        </p>
        <div className="users-customers-toolbar">
          <BoardToolbarExpandSearch
            forceExpanded
            value={query}
            onChange={setQuery}
            placeholder="Filtrar (código, CNPJ, razão social...)"
            ariaLabel="Filtrar clientes"
          />
          <EcClickUpFilterChip
            label="Adicionar cliente"
            icon={AddClienteChipIcon}
            showValue={false}
            ariaLabel="Adicionar cliente"
            onClick={openAddModal}
          />
        </div>
      </div>

      <div className="users-customers-surface">
        <div className="users-customers-table-wrap" role="region" aria-label="Tabela de clientes">
          <table className="users-customers-table">
            <thead>
              <tr>
                <th className="uc-col-codigo">Código</th>
                <th className="uc-col-cnpj">CNPJ</th>
                <th className="uc-col-nome-portal">Nome portal</th>
                <th className="uc-col-razao">Razão Social</th>
                <th className="uc-col-contratos">Contratos</th>
                <th className="uc-col-usuarios">Usuários</th>
                <th className="uc-col-action" aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {mainLoading ? (
                <tr>
                  <td className="users-customers-empty" colSpan={7}>
                    <ProcessConfigSkeleton variant="tableBody" ariaLabel="Carregando clientes" />
                  </td>
                </tr>
              ) : mainError ? (
                <tr>
                  <td className="users-customers-empty" colSpan={7}>
                    {mainError}
                  </td>
                </tr>
              ) : filteredMain.length === 0 ? (
                <tr>
                  <td className="users-customers-empty users-customers-empty--blank" colSpan={7}>
                    {mainRows.length === 0 && !normalizeText(query)
                      ? ''
                      : 'Nenhum cliente encontrado no filtro.'}
                  </td>
                </tr>
              ) : (
                <>
                  {groupedMain.grupos.map((row) => {
                    const expanded = expandedKeys.has(row.rowKey);
                    const integrantes = [row.primeiro, ...row.outros].filter(Boolean);
                    return (
                      <React.Fragment key={row.rowKey}>
                        {renderGrupoSectionHeader(row)}
                        {expanded
                          ? integrantes.map((sub, idx) => {
                              if (!sub) return null;
                              const subDelKey = `sub:${row.rowKey}:${sub.id}`;
                              return (
                                <tr
                                  key={`${row.rowKey}-sub-${sub.id}-${idx}`}
                                  className={[
                                    'users-customers-row',
                                    'users-customers-row--sub',
                                    'users-customers-row--cat-grupos',
                                    deletingKeys.has(subDelKey) ? 'users-customers-row--deleting' : '',
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                >
                                  {renderMainRowCells(row, { isSub: true, subCliente: sub })}
                                </tr>
                              );
                            })
                          : null}
                      </React.Fragment>
                    );
                  })}
                  {groupedMain.semGrupos.length > 0 ? (
                    <React.Fragment key={UC_SEM_GRUPOS.key}>
                      <tr
                        className={[
                          'uc-section-row',
                          semGruposCollapsed ? 'uc-section-row--collapsed' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => setSemGruposCollapsed((v) => !v)}
                      >
                        <td colSpan={7}>
                          <div className="uc-section-label">
                            <span className="uc-section-chev" aria-hidden>
                              <ChevronDown size={13} strokeWidth={2.5} />
                            </span>
                            <span
                              className="uc-section-dot"
                              style={{ background: UC_SEM_GRUPOS.color }}
                              aria-hidden
                            />
                            {UC_SEM_GRUPOS.label}
                            <span className="uc-section-count">· {groupedMain.semGrupos.length}</span>
                            <span className="uc-section-desc">{UC_SEM_GRUPOS.desc}</span>
                          </div>
                        </td>
                      </tr>
                      {groupedMain.semGrupos.map((row) => {
                        const masterDelKey = `row:${row.rowKey}`;
                        return (
                          <tr
                            key={row.rowKey}
                            className={[
                              'users-customers-row',
                              'users-customers-row--cat-semGrupos',
                              semGruposCollapsed ? 'users-customers-row--section-hidden' : '',
                              deletingKeys.has(masterDelKey) ? 'users-customers-row--deleting' : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                          >
                            {renderMainRowCells(row)}
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ) : null}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </section>

      {editGroupRowKey != null && (
        <div
          className="users-customers-modal-backdrop"
          role="presentation"
          aria-hidden={editGroupSaving || Boolean(editGroupRemovingCodigo)}
        >
          <div
            className="users-customers-modal users-customers-modal--with-sidebar"
            role="dialog"
            aria-modal="true"
            aria-labelledby="users-customers-edit-grupo-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="uc-customer-modal-layout">
              {renderCustomerModalSidebar(
                'Grupo de clientes',
                'Gerencie integrantes e configurações.',
                {
                  titleId: 'users-customers-edit-grupo-title',
                  showFilesAccess: Boolean(customerModalEmbeddedScope),
                  showUsersAccess: Boolean(customerModalEmbeddedScope),
                }
              )}
              <div className="uc-customer-modal-main">
                {renderCustomerModalMainHead({
                  onClose: closeEditGroupModal,
                  closeDisabled: editGroupSaving || Boolean(editGroupRemovingCodigo),
                })}
                <div className="uc-customer-modal-main-scroll">
                  {editGroupSaving ? (
                    <div className="users-customers-modal-saving" aria-live="polite">
                      <Loader2 className="users-customers-modal-spinner" size={22} />
                      <div className="users-customers-modal-saving-text">Atualizando grupo…</div>
                    </div>
                  ) : customerModalSection === CUSTOMER_MODAL_SECTION_ARQUIVOS ? (
                    renderFilesSection()
                  ) : customerModalSection === CUSTOMER_MODAL_SECTION_USUARIOS ? (
                    renderUsersSection()
                  ) : customerModalSection === CUSTOMER_MODAL_SECTION_CHAT ? (
                    renderChatAccessSection()
                  ) : (
                    <ClienteGrupoSection
                      showGrupoNome
                      grupoNome={editGroupNome}
                      onGrupoNomeChange={setEditGroupNome}
                      grupoNomeId="uc-editgrupo-nome"
                      nomePortal={editGroupNomePortal}
                      onNomePortalChange={setEditGroupNomePortal}
                      nomePortalId="uc-editgrupo-nome-portal"
                      nomePortalMax={NOME_PORTAL_MAX}
                      integrantes={editGroupIntegrantes}
                      onRemoveIntegrante={removeIntegranteDoGrupo}
                      removingCodigo={editGroupRemovingCodigo}
                      pendingIntegrantes={editGroupPendingIntegrantes}
                      onRemovePending={(id) => {
                        const client = editGroupSelectedCache.get(id);
                        if (client) toggleEditGroupSelect(client);
                      }}
                      query={editGroupQuery}
                      onQueryChange={setEditGroupQuery}
                      searchResults={editGroupDisponiveis}
                      searchLoading={editGroupSearchLoading}
                      searchMin={VC_SEARCH_MIN}
                      selectedIds={editGroupSelectedIds}
                      onToggleSelect={toggleEditGroupSelect}
                      searchDisabled={Boolean(editGroupRemovingCodigo)}
                      searchAriaLabel="Buscar clientes para o grupo"
                    />
                  )}
                </div>
                <div className="uc-customer-modal-footer">
                  {editGroupSaving ? (
                    <div className="uc-customer-modal-footer-saving" aria-live="polite">
                      Salvando integrantes no grupo…
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="uc-customer-modal-btn-cancel"
                        onClick={closeEditGroupModal}
                        disabled={Boolean(editGroupRemovingCodigo)}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="uc-customer-modal-btn-save"
                        onClick={handleEditGroupSave}
                        disabled={!canSaveEditGroup || Boolean(editGroupRemovingCodigo)}
                      >
                        Salvar
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div
          className="users-customers-modal-backdrop"
          role="presentation"
          aria-hidden={modalSaving}
        >
          <div
            className="users-customers-modal users-customers-modal--with-sidebar"
            role="dialog"
            aria-modal="true"
            aria-labelledby="users-customers-modal-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="uc-customer-modal-layout">
              {renderCustomerModalSidebar(
                modalEditSingleCodigo ? 'Editar cliente' : 'Adicionar cliente',
                modalEditSingleCodigo
                  ? 'Atualize dados e forme um novo grupo.'
                  : 'Cadastre clientes no portal.',
                {
                  showChatAccess: modalSelectedCount > 0,
                  showFilesAccess: Boolean(customerModalEmbeddedScope),
                  showUsersAccess: Boolean(customerModalEmbeddedScope),
                  titleId: 'users-customers-modal-title',
                }
              )}
              <div className="uc-customer-modal-main">
                {renderCustomerModalMainHead({
                  onClose: closeModal,
                  closeDisabled: modalSaving,
                })}
                <div className="uc-customer-modal-main-scroll">
                  {modalSaving ? (
                    <div className="users-customers-modal-saving" aria-live="polite">
                      <Loader2 className="users-customers-modal-spinner" size={22} />
                      <div className="users-customers-modal-saving-text">Salvando clientes…</div>
                    </div>
                  ) : customerModalSection === CUSTOMER_MODAL_SECTION_ARQUIVOS ? (
                    renderFilesSection()
                  ) : customerModalSection === CUSTOMER_MODAL_SECTION_USUARIOS ? (
                    renderUsersSection()
                  ) : customerModalSection === CUSTOMER_MODAL_SECTION_CHAT &&
                    modalSelectedCount > 0 ? (
                    renderChatAccessSection()
                  ) : (
                    <ClienteGrupoSection
                      showGrupoNome={modalSelectedCount > 1}
                      grupoNome={modalGrupo}
                      onGrupoNomeChange={setModalGrupo}
                      grupoNomeId="uc-modal-grupo"
                      showNomePortal={modalSelectedCount > 0}
                      nomePortal={modalNomePortal}
                      onNomePortalChange={setModalNomePortal}
                      nomePortalId="uc-modal-nome-portal"
                      nomePortalMax={NOME_PORTAL_MAX}
                      integrantes={[]}
                      pendingIntegrantes={modalPendingIntegrantes}
                      onRemovePending={(id) => {
                        const client = modalSelectedCache.get(id);
                        if (client) toggleModalSelect(client);
                      }}
                      query={modalQuery}
                      onQueryChange={setModalQuery}
                      searchResults={modalDisponiveis}
                      searchLoading={modalListLoading}
                      searchMin={VC_SEARCH_MIN}
                      selectedIds={modalSelectedIds}
                      onToggleSelect={toggleModalSelect}
                      searchAriaLabel="Buscar clientes para adicionar"
                    />
                  )}
                </div>
                <div className="uc-customer-modal-footer">
                  {modalSaving ? (
                    <div className="uc-customer-modal-footer-saving" aria-live="polite">
                      Salvando alterações na listagem…
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="uc-customer-modal-btn-cancel"
                        onClick={closeModal}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="uc-customer-modal-btn-save"
                        onClick={handleModalSave}
                        disabled={!canSaveModal}
                      >
                        Salvar
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
        )}
      </div>
    </div>
  );
}
